import { DataBlockDictionaryDto, QueryFactory } from '@lmt/shared-types';
import { CreateDataBlockFieldDto, DataType } from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useAddDictionaryHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useAddDictionaryHandler';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { CreateDataBlockInputTable } from '@lmt/unicorn-shared/app/components/measurement-fields/components/CreateDataBlockInputTable';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  DataBlockFieldFormData,
  onlyActiveDictionaries,
  useDataTypesList,
  ValueDictionaryColumn,
} from './common';

import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { CheckBox } from '../common/ui-kit/CheckBox';

export type CreateDataBlockFieldDataProp = Partial<DataBlockFieldFormData> & {
  onSave?: (data: CreateDataBlockFieldDto) => unknown;
};
type CreateDataBlockFieldProps = FormComponentProps & {
  dataBlockTypeId: number;
  data?: CreateDataBlockFieldDataProp;
  dataBlockFieldId?: number;
};

const nameof = nameofFactory<DataBlockFieldFormData>();
export const CreateDataBlockField: FC<CreateDataBlockFieldProps> = (props) => {
  const { formType, formId, tryCompleteForm, dataBlockTypeId } = props;

  const i18nextGlobal = useTranslation(NS_DataBlockFields);
  const i18next = useScopedTranslation('data_blocks');
  const modals = useModal();
  const queryClient = useQueryClient();

  const dataBlockFieldQuery = QueryFactory.DataBlockFieldQuery.useGetByIdQuery(
    { id: props.dataBlockFieldId! },
    {
      enabled: !!props.dataBlockFieldId,
    },
  );
  const dataBlockField = dataBlockFieldQuery.data;

  const form = useAdvancedForm<DataBlockFieldFormData>(
    useCallback(
      async (data: DataBlockFieldFormData) => {
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              const dataBlockField: CreateDataBlockFieldDto = {
                name: data.name,
                dataBlockTypeId: dataBlockTypeId,
                dataType: data.type,
                dictionaryId: data.dictionaryId ?? null,
                isOptionAllowed: data.isOptionAllowed,
                tableColumns: data.tableColumns?.map((x) => ({
                  ...x,
                  id: x.id || null,
                  dictionaryColumnId:
                    x.dictionaryColumnId === ValueDictionaryColumn
                      ? null
                      : x.dictionaryColumnId,
                })),
              };
              if (props.data?.onSave) {
                props.data.onSave(dataBlockField);
                return;
              }
              if (props.dataBlockFieldId) {
                await QueryFactory.DataBlockFieldClient.patch(
                  props.dataBlockFieldId,
                  dataBlockField,
                );
                await queryClient.invalidateQueries({
                  queryKey:
                    QueryFactory.DataBlockFieldQuery.getByIdQueryKey(
                      dataBlockTypeId,
                    ),
                });
              } else {
                await QueryFactory.DataBlockFieldClient.create(dataBlockField);
              }
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.DataBlockFieldQuery.getDataBlockFieldsForEditingQueryKey(
                    dataBlockTypeId,
                  ),
              });
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.DataBlockFieldQuery.getQueryKey(dataBlockTypeId),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.MergeFieldQuery.getQueriesQueryKey(),
              });
            },
            i18next.t('loading'),
          );
        } catch (err) {
          const errorDetails = handleSubmitFormError<DataBlockFieldFormData>(
            err,
            form.setError,
          );
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [formType, formId, tryCompleteForm, dataBlockTypeId],
    ),

    useMemo(() => {
      if (!dataBlockField)
        return (
          (props.data as any) ?? {
            name: '',
            type: null!,
            tableColumns: [],
            isOptionAllowed: false,
            isSingleValue: false,
          }
        );

      return {
        name: dataBlockField.name,
        type: dataBlockField.dataType,
        isOptionAllowed: false,
        tableColumns:
          dataBlockField.tableColumns?.map((x) => ({
            ...x,
            dictionaryColumnId: x.dictionaryColumnId
              ? x.dictionaryColumnId
              : ValueDictionaryColumn, // convert nulls to 0, because DropDownList can't handle elements with NULL value
          })) ?? [],
      };
    }, [dataBlockField]),
    {
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  const fieldType = form.watch(nameof('type')) as DataType;
  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery({
    select: onlyActiveDictionaries,
  });
  const dictionaryDropDownOptions = useMemo(
    () =>
      dictionariesQuery.data
        ?.map((x) => ({
          title: i18nextGlobal.t(x.title as any) as string,
          id: x.id,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)) ?? [],
    [dictionariesQuery],
  );
  const dataTypes = useDataTypesList();
  const { enumFieldLinkProps, isCreatingNewDictionary } =
    useAddDictionaryHandler(
      useCallback((newDictionary: DataBlockDictionaryDto) => {
        form.setValue('dictionaryId', newDictionary.id, validateAndDirty);
      }, []),
    );
  return (
    <Loading loading={isCreatingNewDictionary}>
      <form
        onSubmit={
          dataBlockField?.isDefault === true
            ? undefined
            : form.handleSubmitDefault
        }
        className={globalFormStyles.autoSizedContainer}
        data-test-id="createDataBlockFieldPopup"
      >
        <div className={globalFormStyles.dataContainer}>
          <Field
            title={i18next.t('fields.name')}
            testId="new-data-block-name-field"
          >
            <Input
              {...form.register(nameof('name'), requiredRule())}
              disabled={!!props.dataBlockFieldId}
              errorText={form.formState.errors.name?.message}
              autoFocus={true}
              autoComplete="off"
            />
          </Field>
          <Field
            title={i18next.t('fields.type')}
            className={globalFormStyles.field}
            testId="new-data-block-type-field"
          >
            <HookFormDropDownInput
              enableSearch={true}
              options={dataTypes}
              control={form.control}
              rules={requiredRule()}
              name={'type'}
              disabled={!!props.dataBlockFieldId}
              required
              variant="formInput"
              getOptionLabel="displayValue"
              idFunction="value"
              errorText={form.formState.errors.type?.message}
            />
          </Field>
          {fieldType === DataType.Dictionary && (
            <>
              <Field
                title={i18next.t('fields.dictionaryId')}
                className={globalFormStyles.field}
                linkProps={enumFieldLinkProps}
              >
                <HookFormDropDownInput
                  enableSearch={true}
                  options={dictionaryDropDownOptions}
                  control={form.control}
                  rules={requiredRule()}
                  required={true}
                  name={'dictionaryId'}
                  variant="formInput"
                  getOptionLabel="title"
                  idFunction="id"
                  disabled={dataBlockField?.isDefault ?? false}
                  errorText={form.formState.errors.dictionaryId?.message}
                />
              </Field>
              <Field title="">
                <CheckBox
                  defaultChecked={true}
                  title={i18next.t('fields.is_option_allowed')}
                  {...form.register('isOptionAllowed')}
                />
              </Field>
            </>
          )}
          {fieldType === DataType.Table && (
            <Field
              title={i18next.t('fields.tableColumns')}
              className={globalFormStyles.field}
            >
              <CreateDataBlockInputTable
                form={form}
                mode={props.dataBlockFieldId ? 'edit' : 'create'}
                disabled={dataBlockField?.isDefault ?? false}
              />
            </Field>
          )}
        </div>
        <div className={globalFormStyles.submitContainer}>
          <SubmitButton
            submitting={form.formState.isSubmitting}
            className={globalFormStyles.submit}
            disabled={dataBlockField?.isDefault ?? false}
            title={
              props.dataBlockFieldId
                ? i18next.t('edit_field_button')
                : i18next.t('create_button')
            }
          />
        </div>
      </form>
    </Loading>
  );
};
